'use client';

import { c as _c } from "react/compiler-runtime";
import { convictionFraction } from '@morfi/backend-sdk-v2/client';
import { AnimatePresence, m, useReducedMotion } from 'motion/react';
import { useTranslations } from 'next-intl';
import type { ComponentProps, ReactNode } from 'react';
import { OptimizedImage } from '@/components/images/optimized-image';
import { AnimatedButton } from '@/components/ui/animated-button';
import { Icon } from '@/components/ui/icon';
import { ChampionIconSolid } from '@/components/ui/icon/glyphs';
import { useOutcomeColor } from '@/hooks/chart/use-outcome-color';
import { useIntentRoutePrefetch } from '@/hooks/use-intent-route-prefetch';
import { Link } from '@/i18n/navigation';
import { formatPercentValue } from '@/lib/format/format-number';
import { MOTION_ENTRANCE, MOTION_EXIT, MOTION_POSITION } from '@/lib/ui/easing';
import { cn } from '@/lib/ui/utils';
import { AnimatedOutcomePercent } from './animated-outcome-percent';
import { type LiveTrade, LiveTradesOverlay } from './live-trades/live-trades-overlay';
import type { OutcomeRowSummary } from './outcome-row-summary';
const EMPTY_LIVE_TRADES: readonly LiveTrade[] = [];
const EMPTY_LIVE_IDS: readonly string[] = [];
export interface OutcomeStackProps {
  pubkey: string;
  outcomes: readonly OutcomeRowSummary[];
  consensusThreshold: string | bigint | null;
  winnerOriginalIndex?: number | undefined;
  linkDisabled?: boolean;
  onSelectOutcome?: ((originalIndex: number) => void) | undefined;
  liveTrades?: readonly LiveTrade[] | undefined;
  liveSinceMountIds?: readonly string[] | undefined;
  /** Feed list rows sit on `background-secondary`; card surfaces use `primary`. */
  surface?: 'card' | 'list';
}
export function OutcomeStack(t0) {
  const $ = _c(35);
  const {
    pubkey,
    outcomes,
    consensusThreshold,
    winnerOriginalIndex,
    linkDisabled: t1,
    onSelectOutcome,
    liveTrades: t2,
    liveSinceMountIds: t3,
    surface: t4
  } = t0;
  const linkDisabled = t1 === undefined ? false : t1;
  const liveTrades = t2 === undefined ? EMPTY_LIVE_TRADES : t2;
  const liveSinceMountIds = t3 === undefined ? EMPTY_LIVE_IDS : t3;
  const surface = t4 === undefined ? "card" : t4;
  const reduceMotion = useReducedMotion();
  const t = useTranslations("market.row");
  let t5;
  if ($[0] !== outcomes) {
    t5 = outcomes.map(_temp);
    $[0] = outcomes;
    $[1] = t5;
  } else {
    t5 = $[1];
  }
  let t6;
  if ($[2] !== pubkey) {
    t6 = {
      marketPubkey: pubkey
    };
    $[2] = pubkey;
    $[3] = t6;
  } else {
    t6 = $[3];
  }
  const colors = useOutcomeColor(t5, t6);
  let T0;
  let t10;
  let t11;
  let t7;
  let t8;
  let t9;
  if ($[4] !== colors || $[5] !== consensusThreshold || $[6] !== linkDisabled || $[7] !== liveSinceMountIds || $[8] !== liveTrades || $[9] !== onSelectOutcome || $[10] !== outcomes || $[11] !== pubkey || $[12] !== reduceMotion || $[13] !== surface || $[14] !== t || $[15] !== winnerOriginalIndex) {
    let t12;
    if ($[22] !== colors) {
      t12 = (o_0, i) => [o_0.originalIndex, colors[i]];
      $[22] = colors;
      $[23] = t12;
    } else {
      t12 = $[23];
    }
    const colorByIndex = new Map(outcomes.map(t12));
    const sorted = [...outcomes].sort(_temp2);
    let t13;
    if ($[24] !== consensusThreshold) {
      t13 = consensusThreshold === null ? 1 : convictionFraction(consensusThreshold);
      $[24] = consensusThreshold;
      $[25] = t13;
    } else {
      t13 = $[25];
    }
    const threshold = t13;
    const visible = sorted.slice(0, 2);
    t10 = "pointer-events-none relative z-10 flex flex-col gap-4";
    t11 = "outcome-stack";
    T0 = AnimatePresence;
    t7 = false;
    t8 = "popLayout";
    t9 = visible.map(outcome => {
      const color = colorByIndex.get(outcome.originalIndex);
      const winner = winnerOriginalIndex === outcome.originalIndex;
      const loser = winnerOriginalIndex !== undefined && !winner;
      const labelValues = {
        outcome: outcome.label,
        share: formatPercentValue(outcome.share * 100)
      };
      const ariaLabel = winner ? t("viewOutcomeWinner", labelValues) : loser ? t("viewOutcomeLoser", labelValues) : t("viewOutcome", labelValues);
      const href = `/markets/${pubkey}?outcome=${encodeURIComponent(outcome.label)}`;
      const tradeStreamColorClass = winner ? "text-feedback-success-content" : loser ? "text-foreground-tertiary" : color?.text ?? "text-foreground-secondary";
      const label = <><OptimizedImage variant="thumbnail" entity={{
          kind: "outcome",
          marketPubkey: pubkey,
          outcomeIndex: outcome.originalIndex,
          imageUrl: outcome.imageUrl ?? null,
          nsfw: outcome.imageNsfw ?? false,
          hue: color?.hue ?? "blue"
        }} alt="" size="size-8" rounded="rounded-item" preview={false} /><span className="flex min-w-0 flex-1 flex-col gap-1"><span className="flex min-w-0 items-center gap-1 text-heading-xs text-foreground-primary">{winner ? <Icon icon={ChampionIconSolid} className="size-icon-xs text-feedback-success-content" aria-hidden="true" /> : null}<span className="truncate">{outcome.label}</span></span><span className="relative block w-full"><LiveTradesOverlay trades={liveTrades} liveSinceMountIds={liveSinceMountIds} outcomeIndex={outcome.originalIndex} colorClass={tradeStreamColorClass} anchor="stacked-progress" /><span className={cn("block h-0.5 w-full overflow-hidden rounded-full", surface === "list" ? "bg-background-primary" : "bg-background-tertiary")}><svg className="block size-full" aria-hidden="true"><m.line x1={2} x2="100%" y1={1} y2={1} transform="translate(-1 0)" strokeWidth={2} strokeLinecap="round" className={cn("stroke-current", winner ? "text-feedback-success-content" : loser ? "text-background-tertiary" : color?.text)} initial={false} animate={{
                  pathLength: Math.min(1, Math.max(0, outcome.share / (threshold > 0 ? threshold : 1))),
                  opacity: outcome.share > 0 ? 1 : 0
                }} transition={reduceMotion ? {
                  duration: 0
                } : MOTION_POSITION} data-testid={`outcome-stack-fill-${String(outcome.originalIndex)}`} /></svg></span></span></span></>;
      const percentage = <AnimatedOutcomePercent value={outcome.share * 100} decimals={true} className="tabular-nums" />;
      const buttonClass = cn("pointer-events-auto w-16 shrink-0 px-2", winner ? "bg-feedback-success-surface text-feedback-success-content hover:bg-feedback-success-surface" : loser ? "bg-background-tertiary text-foreground-tertiary hover:bg-background-tertiary" : [color?.fill, color?.text, color?.hoverFill]);
      return <m.div key={`${pubkey}:${String(outcome.originalIndex)}`} layout={reduceMotion ? false : "position"} initial={reduceMotion ? false : {
        opacity: 0
      }} animate={{
        opacity: loser ? 0.6 : 1
      }} exit={{
        opacity: 0
      }} transition={reduceMotion ? {
        duration: 0
      } : {
        ...MOTION_ENTRANCE,
        layout: MOTION_POSITION,
        opacity: MOTION_EXIT
      }} className="relative flex items-center gap-3" data-testid={`outcome-stack-row-${String(outcome.originalIndex)}`}>{onSelectOutcome !== undefined ? <AnimatedButton variant="tertiary" className={cn("flex min-w-0 flex-1 items-center gap-3 text-left", "pointer-events-auto h-auto justify-start p-0 hover:bg-transparent")} aria-label={ariaLabel} onClick={() => onSelectOutcome(outcome.originalIndex)} disableHoverScale={true} disableTapScale={true}>{label}</AnimatedButton> : linkDisabled ? <span className="flex min-w-0 flex-1 items-center gap-3 text-left">{label}</span> : <StackedOutcomeLink href={href} aria-label={ariaLabel} className={cn("flex min-w-0 flex-1 items-center gap-3 text-left", "pointer-events-auto")}>{label}</StackedOutcomeLink>}<AnimatedButton variant="secondary" size="sm" disableHoverScale={true} className={buttonClass} asChild={onSelectOutcome === undefined && !linkDisabled} aria-disabled={linkDisabled && onSelectOutcome === undefined ? true : undefined} tabIndex={linkDisabled && onSelectOutcome === undefined ? -1 : undefined} aria-label={ariaLabel} onClick={onSelectOutcome === undefined ? undefined : () => onSelectOutcome(outcome.originalIndex)}>{onSelectOutcome === undefined && !linkDisabled ? <StackedOutcomeLink href={href}>{percentage}</StackedOutcomeLink> : percentage}</AnimatedButton></m.div>;
    });
    $[4] = colors;
    $[5] = consensusThreshold;
    $[6] = linkDisabled;
    $[7] = liveSinceMountIds;
    $[8] = liveTrades;
    $[9] = onSelectOutcome;
    $[10] = outcomes;
    $[11] = pubkey;
    $[12] = reduceMotion;
    $[13] = surface;
    $[14] = t;
    $[15] = winnerOriginalIndex;
    $[16] = T0;
    $[17] = t10;
    $[18] = t11;
    $[19] = t7;
    $[20] = t8;
    $[21] = t9;
  } else {
    T0 = $[16];
    t10 = $[17];
    t11 = $[18];
    t7 = $[19];
    t8 = $[20];
    t9 = $[21];
  }
  let t12;
  if ($[26] !== T0 || $[27] !== t7 || $[28] !== t8 || $[29] !== t9) {
    t12 = <T0 initial={t7} mode={t8}>{t9}</T0>;
    $[26] = T0;
    $[27] = t7;
    $[28] = t8;
    $[29] = t9;
    $[30] = t12;
  } else {
    t12 = $[30];
  }
  let t13;
  if ($[31] !== t10 || $[32] !== t11 || $[33] !== t12) {
    t13 = <div className={t10} data-testid={t11}>{t12}</div>;
    $[31] = t10;
    $[32] = t11;
    $[33] = t12;
    $[34] = t13;
  } else {
    t13 = $[34];
  }
  return t13;
}
function _temp2(a, b) {
  return b.share - a.share || a.originalIndex - b.originalIndex;
}
function _temp(o) {
  return {
    title: o.label,
    index: o.originalIndex
  };
}
function StackedOutcomeLink(props) {
  const $ = _c(3);
  const intent = useIntentRoutePrefetch(props.href);
  let t0;
  if ($[0] !== intent || $[1] !== props) {
    t0 = <Link {...props} prefetch={false} {...intent} />;
    $[0] = intent;
    $[1] = props;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  return t0;
}